<template>
<div class="trail mb-3" @click="trailCardClicked(hit.document)" style="cursor: pointer">
  <div class="trail-card">

    <div class="img-container rounded-lg position-relative">
      <img loading="lazy" decoding="async" class="bg-img" :src="getImage()" :alt="displayTitle">
      <div class="gradient"></div>

      <span v-if="!isCommunityCard" class="badge badge-secondary m-2">
        <small class="d-flex align-items-center">
          <strong>{{ hit.document.average_rating }}</strong>
          <span class="material-icons m-0">star</span>
          <span class="mx-1"><strong>&middot;</strong></span>
          <span class="difficulty-text"><strong>{{ hit.document.difficulty }}</strong></span>
        </small>
      </span>

      <span v-else class="badge badge-secondary m-2">
        <small><strong>{{ itemTypeLabel }}</strong></small>
      </span>

      <div class="d-block m-2 trail-details">
        <p class="display-6 d-block d-md-flex align-items-center mt-2 mb-0 text-white">
          <strong>{{ displayTitle }}</strong>
        </p>
        <p v-if="displayLocation" class="m-0 p-0">
          <small>{{ displayLocation }}</small>
        </p>
        <p v-if="metricsLabel" class="text-yellow m-0 p-0">
          <small>{{ metricsLabel }}</small>
        </p>
      </div>

      <div v-if="miniMapUrl" class="mini-map-container rounded">
        <img :src="miniMapUrl" alt="" class="mini-map">
      </div>

    </div>
  </div>
</div>

</template>

<script lang="ts">
import { MeasurementPrefUtility } from "../../../utils/helpers/MeasurementPref";
import {HikeTimeUtility, PrettyTime} from "../../../utils/helpers/HikeTime";

export default {
  props: {
    hit: Object,
    source: {
      type: String,
      default: 'hikes'
    }
  },
  computed: {
    isCommunityCard() {
      return this.source === 'community' || !!this.hit?.document?.item_type;
    },
    itemTypeLabel() {
      return this.hit?.document?.item_type || 'Community';
    },
    displayTitle() {
      return this.hit?.document?.name || this.hit?.document?.title || 'Community activity';
    },
    displayLocation() {
      return this.hit?.document?.short_address || this.hit?.document?.location || '';
    },
    miniMapUrl() {
      return this.hit?.document?.static_map_url || this.hit?.document?.static_map || null;
    },
    metricsLabel() {
      const metrics = [];
      const distance = this.formatDistance(this.hit?.document?.distance_meters);
      const elevation = this.formatElevation(this.hit?.document?.elevation_gain_meters);
      const duration = this.duration();

      if (distance) metrics.push(distance);
      if (elevation) metrics.push(elevation);
      if (duration) metrics.push(`~${duration}`);

      return metrics.join(' | ');
    }
  },
  methods: {
    formatDistance: function (distance: number) {
      if (!distance || Number.isNaN(distance)) return null;
      let measurementPref = MeasurementPrefUtility.preference()
      return measurementPref.smallUnitFormatter(distance, true)
    },
    formatElevation: function (elevation: number) {
      if (!elevation || Number.isNaN(elevation)) return null;
      let measurementPref = MeasurementPrefUtility.preference()
      return measurementPref.smallUnitFormatter(elevation, false)
    },
    duration() {
      const durationMinutes = this.hit?.document?.duration_minutes;
      if (durationMinutes && durationMinutes > 0) {
        const totalMinutes = Math.round(durationMinutes);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m`;
        if (hours > 0) return `${hours}h`;
        return `${minutes}m`;
      }

      const elevationGainMeters = this.hit?.document?.elevation_gain_meters;
      const distanceMeters = this.hit?.document?.distance_meters;
      if (!elevationGainMeters || !distanceMeters) return null;

      const hikeTimeSeconds = HikeTimeUtility.preference().secondsDuration(elevationGainMeters, distanceMeters)
      return new PrettyTime(hikeTimeSeconds).prettyTime()
    },
    getImage() {
      if (this.hit.document.images && this.hit.document.images.length > 0) {
        return this.hit.document.images[0]
      }
      return this.hit.document.static_map_url || this.hit.document.static_map
    },

    async trailCardClicked(trail) {
      if (this.isCommunityCard) {
        if (trail?.item_type === 'Hike' && trail?.item_id) {
          await this.$store.dispatch('exploreV2/getTrail', Number(trail.item_id));
          if ($(window).width() < 600) {
            window.dispatchEvent(new CustomEvent('explore:hideSidebar'));
          }
          return;
        }

        if (trail?.item_type === 'Recording' && trail?.item_id) {
          await this.$store.dispatch('exploreV2/getRecording', Number(trail.item_id));
          if ($(window).width() < 600) {
            window.dispatchEvent(new CustomEvent('explore:hideSidebar'));
          }
          return;
        }

        if (trail?.item_url) {
          window.location.href = trail.item_url;
        }
        return;
      }

      const rawId = typeof trail.getItemId === 'function'
        ? trail.getItemId()
        : (typeof trail.id === 'string' ? trail.id.split('_').pop() : trail.id);

      if (rawId === undefined || rawId === null || rawId === '') return;

      const id = typeof rawId === 'string' && /^\d+$/.test(rawId) ? parseInt(rawId, 10) : rawId;

      await this.$store.dispatch('exploreV2/getTrail', id);

      if ($(window).width() < 600) {
        window.dispatchEvent(new CustomEvent('explore:hideSidebar'));
      }
    }
  },
};
</script>

<style>
  .mini-map-container{
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    width: 100px; /* Set to your desired width */
    height: 100px; /* Set to your desired height */
    overflow: hidden; /* This will hide any overflow */
    border: 2px solid #fff; /* Your border settings */
    display: flex; /* Enables flexbox layout */
    justify-content: center; /* Center horizontally */
    align-items: center; /* Center vertically */
  }

  .mini-map{
    min-width: 100%; /* Minimum width of 100% of parent */
    min-height: 100%; /* Minimum height of 100% of parent */
    width: auto; /* Width set to auto */
    height: auto; /* Height set to auto */
    max-width: none; /* Override any max-width */
    object-fit: cover; /* Cover the container without losing aspect ratio */
  }
</style>
